import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SceneWithUserNames, SceneInteriorExterior, SceneDayNight } from '../../../types/scene.types';
import { ElementInfo, ElementType } from '../../../types/element.types';
import { sortScenes } from '../../Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { formatPageNumberDisplay } from '../../../utils/pageNumberUtils';
import { getSceneCodeCellBackgroundColor } from '../../Breakdown/ScenesTable/Utils/SceneCellUtils';
import { useDataCache } from '../../../contexts/BreakdownDataCacheContext';
import { useElementUpdates } from '../../../hooks/dataUpdates/useElementUpdates';
import { ReactComponent as SortIcon } from '../../../assets/svg/Breakdown/sort.svg';
import { ReactComponent as EditIcon } from '../../../assets/svg/Elements/edit.svg';
import './ElementSceneList.css';

interface ElementSceneListProps {
  breakdownId: string;
  selectedElement: ElementInfo | null;
  canEdit?: boolean;
  /** 点击某行的编辑图标时打开场次详情抽屉（从页面最右侧打开） */
  onEditScene?: (sceneId: string) => void;
}

const ElementSceneList: React.FC<ElementSceneListProps> = ({ breakdownId, selectedElement, canEdit = false, onEditScene }) => {
  const { t } = useTranslation();
  const { updateElementRelationData } = useElementUpdates();

  /** 数量列输入草稿（未失焦前允许空串等中间态） */
  const [quantityDraftBySceneId, setQuantityDraftBySceneId] = useState<Record<string, string>>({});

  // 排序配置
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'sceneCode',
    direction: 'asc'
  });

  // 获取该场次元素关联的场次列表
  const [relatedScenes, setRelatedScenes] = useState<SceneWithUserNames[]>([]);
  // 场次元素在各场次中的数量（sceneID -> quantity）
  const [quantityBySceneId, setQuantityBySceneId] = useState<Map<string, number>>(new Map());

  // 使用数据缓存 hooks
  const {
    getElementRelationCache,
    subscribeElementRelationCache,
    getBreakdownCache,
    subscribeBreakdownCache
  } = useDataCache();

  // 从缓存获取关联场次数据
  useEffect(() => {
    if (!breakdownId || !selectedElement) {
      setRelatedScenes([]);
      setQuantityBySceneId(new Map());
      return;
    }

    const updateRelatedScenes = () => {
      const relationCache = getElementRelationCache(breakdownId);
      const breakdownCache = getBreakdownCache(breakdownId);

      if (!relationCache?.elementRelations || !breakdownCache?.scenes) {
        setRelatedScenes([]);
        return;
      }

      // 从关系表中过滤出包含当前选中场次元素的关系记录
      const relations = relationCache.elementRelations.filter(
        rel => rel.elementID === selectedElement.elementID
      );

      // 构建 sceneID -> elementQtyInScene 映射
      const qtyMap = new Map<string, number>();
      relations.forEach(rel => {
        qtyMap.set(rel.sceneID, rel.elementQtyInScene ?? 1);
      });
      setQuantityBySceneId(qtyMap);

      // 提取场次ID列表（去重）
      const sceneIds = Array.from(new Set(relations.map(rel => rel.sceneID)));

      // 从场次缓存中获取完整的场次数据
      const filteredScenes = breakdownCache.scenes.filter(scene => sceneIds.includes(scene.sceneID));

      // 对场次进行排序（数量列按 quantityBySceneId 排序，其余列用 sortScenes）
      const sortedScenes = sortConfig.key === 'elementQtyInScene'
        ? [...filteredScenes].sort((a, b) => {
            const qa = qtyMap.get(a.sceneID) ?? 0;
            const qb = qtyMap.get(b.sceneID) ?? 0;
            return sortConfig.direction === 'asc' ? qa - qb : qb - qa;
          })
        : sortScenes(filteredScenes, sortConfig.key, sortConfig.direction);

      setRelatedScenes(sortedScenes);
    };

    // 立即执行一次
    updateRelatedScenes();

    // 订阅缓存更新
    const unsubscribeRelation = subscribeElementRelationCache(breakdownId, updateRelatedScenes);
    const unsubscribeBreakdown = subscribeBreakdownCache(breakdownId, updateRelatedScenes);

    return () => {
      unsubscribeRelation();
      unsubscribeBreakdown();
    };
  }, [breakdownId, selectedElement, sortConfig, getElementRelationCache, getBreakdownCache, subscribeElementRelationCache, subscribeBreakdownCache]);

  useEffect(() => {
    setQuantityDraftBySceneId({});
  }, [breakdownId, selectedElement?.elementID]);

  const clearQuantityDraft = useCallback((sceneId: string) => {
    setQuantityDraftBySceneId((prev) => {
      if (!(sceneId in prev)) return prev;
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
  }, []);

  const commitSceneQuantity = useCallback(
    async (sceneId: string, raw: string) => {
      if (!selectedElement || !breakdownId) return;
      const trimmed = raw.trim();
      const parsed = parseInt(trimmed, 10);
      const committed = quantityBySceneId.get(sceneId) ?? 1;

      if (trimmed === '' || !Number.isFinite(parsed) || parsed < 1) {
        clearQuantityDraft(sceneId);
        return;
      }

      if (parsed === committed) {
        clearQuantityDraft(sceneId);
        return;
      }

      try {
        await updateElementRelationData(breakdownId, selectedElement.elementID, sceneId, {
          elementQtyInScene: parsed,
        });
        clearQuantityDraft(sceneId);
      } catch {
        clearQuantityDraft(sceneId);
      }
    },
    [
      selectedElement,
      breakdownId,
      quantityBySceneId,
      updateElementRelationData,
      clearQuantityDraft,
    ]
  );

  // 排序处理函数（数量列按 quantityBySceneId 排序，其余列用 sortScenes）
  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortConfig({ key, direction });
    if (key === 'elementQtyInScene') {
      setRelatedScenes(prev => {
        return [...prev].sort((a, b) => {
          const qa = quantityBySceneId.get(a.sceneID) ?? 0;
          const qb = quantityBySceneId.get(b.sceneID) ?? 0;
          return direction === 'asc' ? qa - qb : qb - qa;
        });
      });
    } else {
      setRelatedScenes(prev => sortScenes(prev, key, direction));
    }
  }, [quantityBySceneId]);

  // 是否显示数量列（仅特约、群演、动作演员、动物、车辆）
  const showQuantityColumn = useMemo(() => {
    if (!selectedElement?.elementType) return false;
    return [
      ElementType.FEATURED,
      ElementType.BACKGROUND,
      ElementType.STUNT_PLAYERS,
      ElementType.ANIMALS,
      ElementType.VEHICLES,
    ].includes(selectedElement.elementType as ElementType);
  }, [selectedElement?.elementType]);

  // 创建只读表格列定义（首列为编辑图标）
  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: 'edit',
        title: null as React.ReactNode,
        width: 28,
        className: 'element-scene-list-edit-cell',
        sortable: false,
      },
      {
        key: 'sceneCode',
        title: <span>{t('breakdown.sceneNumber')}</span>,
        width: 100,
        className: 'element-scene-list-code-cell',
        sortable: true,
        sortType: 'sceneCode' as const,
      },
      {
        key: 'sceneInteriorExterior',
        title: t('breakdown.interiorExterior'),
        width: 40,
        className: 'element-scene-list-interior-exterior-cell',
        sortable: false,
      },
      {
        key: 'sceneDayNight',
        title: t('breakdown.dayNight'),
        width: 40,
        className: 'element-scene-list-day-night-cell',
        sortable: false,
      },
      {
        key: 'sceneSet',
        title: t('breakdown.set'),
        width: '30%',
        className: 'element-scene-list-set-cell',
        sortable: true,
        sortType: 'text' as const,
      },
      {
        key: 'scenePlotDay',
        title: t('breakdown.plotDay'),
        width: 60,
        className: 'element-scene-list-plot-day-cell',
        sortable: false,
      },
      {
        key: 'scenePagesNumber',
        title: t('breakdown.scriptPages'),
        width: 44,
        className: 'element-scene-list-pages-cell',
        sortable: false,
      },
      {
        key: 'scenePlot',
        title: t('breakdown.plot'),
        width: '30%',
        className: 'element-scene-list-description-cell',
        sortable: false,
      },
      {
        key: 'sceneLocation',
        title: t('breakdown.shootingLocation'),
        width: '30%',
        className: 'element-scene-list-location-cell',
        sortable: true,
        sortType: 'text' as const,
      },
    ];
    if (showQuantityColumn) {
      baseColumns.push({
        key: 'elementQtyInScene',
        title: t('breakdown.quantity'),
        width: 56,
        className: 'element-scene-list-quantity-cell',
        sortable: true,
      } as const);
    }
    return baseColumns;
  }, [t, showQuantityColumn]);

  // 获取单元格显示值（edit 列由渲染逻辑单独处理，不在此返回）
  const getCellDisplayValue = useCallback((scene: SceneWithUserNames, colKey: string): React.ReactNode => {
    if (colKey === 'edit') return null;
    // 数量列：从关系获取该场次的元素数量
    if (colKey === 'elementQtyInScene') {
      return quantityBySceneId.get(scene.sceneID) ?? 1;
    }
    
    // 内外列：与分场表一致，显示 i18n 短标签
    if (colKey === 'sceneInteriorExterior') {
      if (scene.sceneInteriorExterior === SceneInteriorExterior.INTERIOR) {
        return t('export.interiorShort');
      }
      if (scene.sceneInteriorExterior === SceneInteriorExterior.EXTERIOR) {
        return t('export.exteriorShort');
      }
      if (scene.sceneInteriorExterior === SceneInteriorExterior.INTERIOR_EXTERIOR) {
        return t('export.interiorExteriorShort');
      }
      return '';
    }
    
    // 日夜列：与分场表一致，显示 i18n 短标签
    if (colKey === 'sceneDayNight') {
      if (scene.sceneDayNight === SceneDayNight.MORNING) {
        return t('export.morningShort');
      }
      if (scene.sceneDayNight === SceneDayNight.DAY) {
        return t('export.dayShort');
      }
      if (scene.sceneDayNight === SceneDayNight.DUSK) {
        return t('export.duskShort');
      }
      if (scene.sceneDayNight === SceneDayNight.NIGHT) {
        return t('export.nightShort');
      }
      return '';
    }
    
    // 页数列：统一显示1位小数
    if (colKey === 'scenePagesNumber') {
      return formatPageNumberDisplay(scene.scenePagesNumber);
    }
    
    // 其他列：直接显示值
    return (scene as any)[colKey] || '';
  }, [quantityBySceneId, t]);

  // 获取单元格样式
  const getCellStyle = useCallback((scene: SceneWithUserNames, colKey: string): React.CSSProperties | undefined => {
    // 场号单元格需要特殊背景颜色
    if (colKey === 'sceneCode') {
      const backgroundColor = getSceneCodeCellBackgroundColor(
        scene.sceneInteriorExterior,
        scene.sceneDayNight
      );
      return {
        backgroundColor: backgroundColor !== 'transparent' ? backgroundColor : undefined,
      };
    }
    
    return undefined;
  }, []);

  return (
    <div className="element-scene-list">
      <div className="element-scene-list-table-container">
        <div className="element-scene-list-table-wrapper">
          <table className="element-scene-list-table">
            <thead>
              <tr>
                {columns.map((col, index) => {
                  const isCurrentSort = sortConfig?.key === col.key;
                  const sortDirection = isCurrentSort ? sortConfig.direction : null;
                  const isEditCol = col.key === 'edit';
                  return (
                    <th
                      key={col.key || index}
                      className={`element-scene-list-header ${col.className || ''}`}
                      style={col.width ? { width: typeof col.width === 'number' ? `${col.width}px` : col.width } : undefined}
                    >
                      <div className="element-scene-list-header-content">
                        {!isEditCol && (
                        <div className="element-scene-list-header-title">
                          <div className="element-scene-list-header-title-left">
                            {col.title}
                            {/* 排序按钮 */}
                            {col.sortable && handleSort && (
                              <button
                                className={`element-scene-list-sort-icon ${isCurrentSort ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newDirection = isCurrentSort && sortDirection === 'asc' ? 'desc' : 'asc';
                                  handleSort(col.key, newDirection);
                                }}
                                title={isCurrentSort 
                                  ? (sortDirection === 'asc' ? '降序排列' : '升序排列')
                                  : '排序'}
                                tabIndex={-1}
                              >
                                <SortIcon aria-label="排序" />
                              </button>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {relatedScenes.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="element-scene-list-empty-cell">
                    {selectedElement ? '该元素暂未关联任何场次' : '请先选择一个元素'}
                  </td>
                </tr>
              ) : (
                relatedScenes.map((scene) => (
                  <tr
                    key={scene.sceneID}
                    className="element-scene-list-row"
                    style={{ height: 24 }}
                  >
                    {columns.map((col) => {
                      const isEditCol = col.key === 'edit';
                      const cellStyle = getCellStyle(scene, col.key);
                      const displayValue = getCellDisplayValue(scene, col.key);
                      
                      const mergedStyle = {
                        ...cellStyle,
                        ...(col.width ? { width: typeof col.width === 'number' ? `${col.width}px` : col.width } : {})
                      };
                      
                      return (
                        <td
                          key={col.key}
                          className={`element-scene-list-cell ${col.className || ''}`}
                          style={mergedStyle}
                        >
                          <div className="element-scene-list-cell-content">
                            {isEditCol ? (canEdit ? (
                              <button
                                type="button"
                                className="element-scene-list-edit-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditScene?.(scene.sceneID);
                                }}
                                title={t('common.edit')}
                                tabIndex={-1}
                                aria-label={t('common.edit')}
                              >
                                <EditIcon className="element-scene-list-edit-icon" />
                              </button>
                            ) : null) : col.key === 'elementQtyInScene' ? (
                              <input
                                type="number"
                                min={1}
                                step={1}
                                className="element-scene-list-quantity-input"
                                disabled={!canEdit}
                                value={
                                  quantityDraftBySceneId[scene.sceneID] !== undefined
                                    ? quantityDraftBySceneId[scene.sceneID]
                                    : String(quantityBySceneId.get(scene.sceneID) ?? 1)
                                }
                                onChange={(e) =>
                                  setQuantityDraftBySceneId((prev) => ({
                                    ...prev,
                                    [scene.sceneID]: e.target.value,
                                  }))
                                }
                                onBlur={(e) => commitSceneQuantity(scene.sceneID, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={t('breakdown.quantity')}
                              />
                            ) : (
                              displayValue
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ElementSceneList;
